import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { authorAPI } from '../../lib/api';
import { UnifiedContentEditor, SidebarCard } from '../../components/admin/UnifiedContentEditor';
import { CitationPicker } from '../../components/admin/CitationPicker';
import { ReferencesManager } from '../../components/admin/ReferencesManager';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { AlertCircle, X } from 'lucide-react';

const CATEGORIES = ['Heritage', 'History', 'Culture', 'Architecture', 'People', 'Events', 'General'];

export default function AuthorArticleEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  const editorRef = useRef(null);
  
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  
  // Article data
  const [title, setTitle] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [content, setContent] = useState('');
  const [featuredImage, setFeaturedImage] = useState('');
  const [category, setCategory] = useState('General');
  const [visibility, setVisibility] = useState('public');
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  
  // Citations
  const [citations, setCitations] = useState([]);
  const [showCitationPicker, setShowCitationPicker] = useState(false);
  
  // Status info (for edit mode)
  const [articleStatus, setArticleStatus] = useState('draft');
  const [rejectionReason, setRejectionReason] = useState(null);

  // Tag functions
  const addTag = () => {
    if (!newTag.trim()) return;
    const newTags = newTag
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag && !tags.includes(tag));
    if (newTags.length > 0) {
      setTags([...tags, ...newTags]);
    }
    setNewTag('');
  };

  const removeTag = (tagToRemove) => {
    setTags(tags.filter(t => t !== tagToRemove));
  };

  useEffect(() => {
    const fetchArticle = async () => {
      if (isEditing) {
        try {
          const response = await authorAPI.getArticles();
          const article = response.data.find(a => a.id === id);
          
          if (!article) {
            toast.error('Article not found');
            navigate('/author/articles');
            return;
          }
          
          // Check if article can be edited
          if (!['draft', 'rejected'].includes(article.status)) {
            toast.error('This article cannot be edited');
            navigate('/author/articles');
            return;
          }
          
          setTitle(article.title || '');
          setExcerpt(article.excerpt || '');
          setContent(article.content || '');
          setFeaturedImage(article.featured_image || '');
          setCategory(article.category || 'General');
          setVisibility(article.visibility || 'public');
          setTags(article.tags || []);
          setCitations(article.citations || []);
          setArticleStatus(article.status);
          setRejectionReason(article.rejection_reason);
        } catch (error) {
          toast.error('Failed to load article');
          navigate('/author/articles');
        } finally {
          setLoading(false);
        }
      }
    };
    fetchArticle();
  }, [id, isEditing, navigate]);

  const handleSelectCitation = (citationIndex) => {
    if (editorRef.current) {
      editorRef.current.insertCitation(citationIndex);
    }
  };

  const handleAddCitation = (citationHtml) => {
    setCitations([...citations, citationHtml]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim() || !content.trim() || !excerpt.trim()) {
      toast.error('Please fill in title, excerpt, and content');
      return;
    }

    setSaving(true);

    const formData = {
      title,
      content,
      excerpt,
      featured_image: featuredImage,
      category,
      visibility,
      citations,
      tags
    };

    try {
      if (isEditing) {
        await authorAPI.updateArticle(id, formData);
        toast.success('Article saved as draft');
      } else {
        await authorAPI.createArticle(formData);
        toast.success('Article created as draft');
      }
      navigate('/author/articles');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="spinner"></div>
      </div>
    );
  }

  const sidebarContent = (
    <>
      {/* Rejection Notice */}
      {articleStatus === 'rejected' && rejectionReason && (
        <SidebarCard title="">
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-800 mb-1">Revision Requested</p>
                <p className="text-sm text-red-700">{rejectionReason}</p>
              </div>
            </div>
          </div>
        </SidebarCard>
      )}
      
      {/* Category */}
      <SidebarCard title="Category">
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="category-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map((cat) => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </SidebarCard>

      {/* Visibility */}
      <SidebarCard title="Visibility">
        <Select value={visibility} onValueChange={setVisibility}>
          <SelectTrigger data-testid="visibility-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="public">Public - Visible to all</SelectItem>
            <SelectItem value="restricted">Restricted - Login required</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground mt-2">
          Restricted articles require users to sign in to read full content.
        </p>
      </SidebarCard>

      {/* Tags */}
      <SidebarCard title="Tags">
        <div className="flex gap-2">
          <Input
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            placeholder="tag1, tag2, tag3"
            className="text-sm"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addTag();
              }
            }}
            data-testid="new-tag"
          />
          <Button type="button" variant="outline" size="sm" onClick={addTag}>Add</Button>
        </div>
        <p className="text-xs text-muted-foreground mt-1">Separate multiple tags with commas</p>
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {tags.map((tag) => (
              <span key={tag} className="tag-badge flex items-center gap-1 text-xs bg-muted px-2 py-1 rounded">
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </SidebarCard>

      {/* References Manager */}
      <SidebarCard title="">
        <ReferencesManager
          citations={citations}
          onCitationsChange={setCitations}
          maxHeight="150px"
        />
      </SidebarCard>

      {/* Info Box */}
      <SidebarCard title="About Publishing">
        <p className="text-sm text-muted-foreground">
          After saving, submit your article for admin review. 
          Once approved, it will be published on the site with your name as the author.
        </p>
      </SidebarCard>
    </>
  );

  return (
    <>
      <UnifiedContentEditor
        ref={editorRef}
        contentType="article"
        pageTitle={isEditing ? "Edit Article" : "Write New Article"}
        
        title={title}
        onTitleChange={setTitle}
        titlePlaceholder="Enter your article title..."
        
        showExcerpt={true}
        excerpt={excerpt}
        onExcerptChange={setExcerpt}
        excerptPlaceholder="Brief summary of your article..."
        excerptHelp="This appears in article previews and search results."
        
        content={content}
        onContentChange={setContent}
        contentPlaceholder="Start writing your heritage article..."
        
        featuredImage={featuredImage}
        onFeaturedImageChange={setFeaturedImage}
        
        onBack={() => navigate('/author/articles')}
        onSubmit={handleSubmit}
        saving={saving}
        isEditing={isEditing}
        submitLabel="Save as Draft"
        
        sidebarContent={sidebarContent}
        
        citations={citations}
        onInsertCitation={() => setShowCitationPicker(true)}
        showCitationButton={true}
      />

      <CitationPicker
        open={showCitationPicker}
        onOpenChange={setShowCitationPicker}
        citations={citations}
        onSelectCitation={handleSelectCitation}
        onAddCitation={handleAddCitation}
      />
    </>
  );
}
